import React, { useState, useEffect } from 'react';
import { lifecycleExplanations } from '../config/lifecycleExplanations';
import { statusExplanations } from '../config/statusExplanations';
import { projectStatuses } from '../config/projectStatuses';
import { isPastDueExpectedEndDate } from '../config/projectDates';
import "./ApiComponent.css";
import { Link, useNavigate } from 'react-router-dom';
import MapComponent from './MapComponent';
import PortfolioSummary from './PortfolioSummary';
import ViewAsContactSearch from './ViewAsContactSearch';

const VIEW_AS_EMAIL_KEY = 'apiComponentViewAsEmail';
const normalizeSearchText = value => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const projectHealthStates = [
  { label: 'On Track', className: 'on-track' },
  { label: 'At Risk', className: 'at-risk' },
  { label: 'Off Track', className: 'off-track' },
];
const knownProjectHealthValues = new Set(projectHealthStates.map(state => state.label.toLowerCase()));

function ApiComponent() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(null);
  const [isMilroseUser, setIsMilroseUser] = useState(false);
  const [viewAsInput, setViewAsInput] = useState(() => sessionStorage.getItem(VIEW_AS_EMAIL_KEY) || '');
  const [viewAsEmail, setViewAsEmail] = useState(() => sessionStorage.getItem(VIEW_AS_EMAIL_KEY) || '');

  const CACHE_KEY = 'apiComponentDataCache';
  const CACHE_TIME_KEY = 'apiComponentDataCacheTime';
  const CACHE_EMAIL_KEY = 'apiComponentDataCacheEmail';
  const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

  const knownStates = [
                          'Project Hold',
                          'Accounting Hold - Past Due Unpaid Invoices',
                          'Hold - Past Due Invoices',
                          'Cancelled',
                          'Complete'
                        ];

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        let authenticatedEmail = null;
        let emailHeader = null;
        async function getUserEmail() {
          const response = await fetch('/.auth/me');
          if (response.ok) {
            const data = await response.json();
            const claims = data?.clientPrincipal?.claims || [];
            const claimPreference = [
              'emails',
              'email',
              'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
              'preferred_username',
              'upn',
              'name'
            ];
            const emailClaim = claimPreference
              .map(type => claims.find(claim => claim.typ === type && typeof claim.val === 'string' && claim.val.includes('@')))
              .find(Boolean);
            const userEmail = emailClaim ? emailClaim.val : null;
            const fallbackUserDetails = data?.clientPrincipal?.userDetails;
            const resolvedEmail = userEmail || (typeof fallbackUserDetails === 'string' && fallbackUserDetails.includes('@') ? fallbackUserDetails : null);
            if (resolvedEmail) {
              setEmail(resolvedEmail);
              const milroseUser = isMilroseDomainEmail(resolvedEmail);
              setIsMilroseUser(milroseUser);
              if (!milroseUser) {
                sessionStorage.removeItem(VIEW_AS_EMAIL_KEY);
                setViewAsInput('');
                setViewAsEmail('');
              }
              return resolvedEmail;
            }
          }
          setIsMilroseUser(false);
          return null;
        }
        authenticatedEmail = await getUserEmail();
        if (!authenticatedEmail) {
          throw new Error('Unable to determine authenticated user email.');
        }

        emailHeader = isMilroseDomainEmail(authenticatedEmail) && viewAsEmail.trim()
          ? viewAsEmail.trim().toLowerCase()
          : authenticatedEmail;

        // Check cache
        const cached = localStorage.getItem(CACHE_KEY);
        const cachedTime = localStorage.getItem(CACHE_TIME_KEY);
        const cachedEmail = localStorage.getItem(CACHE_EMAIL_KEY);
        const now = Date.now();
        if (cached && cachedTime && cachedEmail === emailHeader && now - parseInt(cachedTime, 10) < CACHE_DURATION) {
          const cachedData = JSON.parse(cached);
          if (cachedData.data?.every(project => Object.prototype.hasOwnProperty.call(project, 'Project_Health__c'))) {
            setData(cachedData);
            setLoading(false);
            return;
          }
        }

        const response = await fetch('/api/salesforce-data', {
          headers: {
            'X-Email-Header': emailHeader
          }
        });
        if (!response.ok) {
          if (response.status === 400 || response.status === 403) {
            const errorBody = await response.json();
            throw new Error(errorBody.error || 'Bad Request');
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }
        const json = await response.json();
        setData(json);

        // Cache the data
        localStorage.setItem(CACHE_KEY, JSON.stringify(json));
        localStorage.setItem(CACHE_TIME_KEY, now.toString());
        localStorage.setItem(CACHE_EMAIL_KEY, emailHeader);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    }
    fetchData();

    // Listen for sign out event to clear cache
    function handleStorageEvent(e) {
      if (e.key === 'signedOut' && e.newValue === 'true') {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_TIME_KEY);
        localStorage.removeItem(CACHE_EMAIL_KEY);
        sessionStorage.removeItem(VIEW_AS_EMAIL_KEY);
      }
    }
    window.addEventListener('storage', handleStorageEvent);
    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [CACHE_DURATION, viewAsEmail]);

  ApiComponent.clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIME_KEY);
    localStorage.removeItem(CACHE_EMAIL_KEY);
    sessionStorage.removeItem(VIEW_AS_EMAIL_KEY);
    localStorage.setItem('signedOut', 'true');
    setTimeout(() => localStorage.removeItem('signedOut'), 100);
  };

  const columnDefs = [
    { key: 'Name', label: 'MRC#' },
    { key: 'Client Name', label: 'Client Name' },
    { key: 'Client_Project__c', label: 'Client Project NO' },
    { key: 'Project_Name__c', label: 'Project Name' },
    { key: 'Project_Type_Text__c', label: 'Project Type' },
    { key: 'Jurisdiction', label: 'Jurisdiction' },
    { key: 'Building_Formula__c', label: 'Building Address, Fl#' },
    { key: 'Project_Status__c', label: 'Current Status' },
    { key: 'Project_Health__c', label: 'Project Health' },
    { key: 'Current_Status__c', label: 'Notes' },
    { key: 'Milrose PM', label: 'Milrose PM' },
    { key: 'Client PM', label: 'Client PM' },
    { key: 'GC Contact', label: 'GC Contact' },
  ];
  const [filters, setFilters] = useState(() => Object.fromEntries(columnDefs.map(col => [col.key, ''])));
  const [endDateSort, setEndDateSort] = useState(null);
  const [expectedEndDateFilter, setExpectedEndDateFilter] = useState('');
  const handleFilterChange = (colKey, value) => {
    setFilters(f => ({ ...f, [colKey]: value }));
  };
  const handleSummaryMetricClick = filter => {
    const clearedFilters = Object.fromEntries(Object.keys(filters).map(key => [key, '']));
    if (filter.type === 'health') clearedFilters.Project_Health__c = filter.value;
    setFilters(clearedFilters);
    setExpectedEndDateFilter(filter.type === 'past-due' ? 'past-due' : '');
  };
  const isMilroseDomainEmail = (value) => typeof value === 'string' && value.trim().toLowerCase().endsWith('@milrose.com');
  const formatExpectedEndDate = (value) => {
    if (!value) return 'N/A';

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return 'N/A';

    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const year = parsedDate.getFullYear();

    return `${month}/${day}/${year}`;
  };
  const filteredProjects = React.useMemo(() => {
    if (!data || !data.data) return [];
    return data.data.filter(project => {
      if (expectedEndDateFilter === 'past-due' && !isPastDueExpectedEndDate(project)) return false;

      // Project State filter (third column)
      var projectStateVal = (project.Project_State__c || '').toLowerCase();
      if (!knownStates.includes(project.Project_State__c)) {
        projectStateVal = 'in progress';
      }
      if (!projectStateVal.includes((filters['Project_State__c'] || '').toLowerCase())) return false;

      // Project Name filter (first column)
      const projectNameVal = [
        project.Name,
        project.Billing_Company_Name__r && project.Billing_Company_Name__r.Name,
        project.Client_Project__c || 'N/A',
        project.Project_Name__c,
        project.Building_Formula__c,
        project.Floor_s__c,
        project.Building_City__c,
        project.Building_State__c,
        [project.Building_Formula__c, project.Floor_s__c, project.Building_City__c, project.Building_State__c].filter(Boolean).join(', '),
      ].filter(Boolean).join(' ');
      if (!normalizeSearchText(projectNameVal).includes(normalizeSearchText(filters['Project_Name__c']))) return false;

      // Client Name filter (second column)
      const rosterForProject = data.roster ? data.roster.filter(r => r.Project_Profile__r && r.Project_Profile__r.Id === project.Id) : [];
      const regionalLeadNames = rosterForProject.filter(r => r.Role__c === 'Regional Lead').map(r => r.Name_Full__c || '');
      const teamLeadNames = rosterForProject.filter(r => r.Role__c === 'Team Lead').map(r => r.Name_Full__c || '');
      const clientPMNames = rosterForProject.filter(r => r.Role__c === 'Client Project Manager').map(r => r.Name_Full__c || '');
      const clientNameVal = [
        project.Project_Manager__r?.Name || project.Owner?.Name || 'N/A',
        (project.Client_Project_Manager__r && project.Client_Project_Manager__r.Name) || 'N/A',
        ...clientPMNames,
        ...regionalLeadNames,
        ...teamLeadNames,
      ].join(' ');
      if (!normalizeSearchText(clientNameVal).includes(normalizeSearchText(filters['Client Name']))) return false;

      // Project Status filter (fourth column)
      const statusFilter = filters['Project_Status__c'];
      if (statusFilter) {
        if (statusFilter === 'N/A') {
          if (project.Project_Status__c !== undefined && project.Project_Status__c !== null && project.Project_Status__c !== '') return false;
        } else {
          if ((project.Project_Status__c || '').toLowerCase() !== statusFilter.toLowerCase()) return false;
        }
      }

      const healthFilter = filters['Project_Health__c'];
      if (healthFilter) {
        const health = String(project.Project_Health__c || '').trim().toLowerCase();
        if (healthFilter === 'N/A') {
          if (knownProjectHealthValues.has(health)) return false;
        } else if (health !== healthFilter.toLowerCase()) {
          return false;
        }
      }

      // Other columns (if any filters are added in the future)
      return Object.entries(filters).every(([key, value]) => {
        if (!value || ['Project_Name__c', 'Client Name', 'Project_State__c', 'Project_Status__c', 'Project_Health__c'].includes(key)) return true;
        const colVal = (project[key] || '').toString().toLowerCase();
        return colVal.includes(value.toLowerCase());
      });
    });
  }, [data, filters, expectedEndDateFilter]);

  const sortedProjects = React.useMemo(() => {
    if (!endDateSort) return filteredProjects;
    const dateValue = value => value ? new Date(value).getTime() : NaN;
    return [...filteredProjects].sort((a, b) => {
      const aDate = dateValue(a.Project_Expected_End_Date__c);
      const bDate = dateValue(b.Project_Expected_End_Date__c);
      if (Number.isNaN(aDate)) return Number.isNaN(bDate) ? 0 : 1;
      if (Number.isNaN(bDate)) return -1;
      return endDateSort === 'asc' ? aDate - bDate : bDate - aDate;
    });
  }, [filteredProjects, endDateSort]);

  if (loading) {
    return <p>Loading...</p>;
  }

  const viewAsControls = isMilroseUser && (
    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <label htmlFor="view-as-contact" style={{ color: '#334155', fontWeight: 600 }}>View data as email:</label>
      <ViewAsContactSearch
        value={viewAsInput}
        onChange={setViewAsInput}
        authenticatedEmail={email}
      />
      <button
        type="button"
        disabled={!!viewAsInput.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(viewAsInput.trim())}
        onClick={() => {
          const nextValue = (viewAsInput || '').trim().toLowerCase();
          if (nextValue === viewAsEmail) return;
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem(CACHE_TIME_KEY);
          localStorage.removeItem(CACHE_EMAIL_KEY);
          if (nextValue) {
            sessionStorage.setItem(VIEW_AS_EMAIL_KEY, nextValue);
          } else {
            sessionStorage.removeItem(VIEW_AS_EMAIL_KEY);
          }
          setViewAsEmail(nextValue);
        }}
        style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #0a2740', background: '#0a2740', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
      >
        Apply
      </button>
      <button
        type="button"
        onClick={() => {
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem(CACHE_TIME_KEY);
          localStorage.removeItem(CACHE_EMAIL_KEY);
          sessionStorage.removeItem(VIEW_AS_EMAIL_KEY);
          setViewAsInput('');
          setViewAsEmail('');
        }}
        style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #94a3b8', background: '#fff', color: '#1e293b', fontWeight: 600, cursor: 'pointer' }}
      >
        Reset
      </button>
    </div>
  );

  if (error) {
    return (
      <div>
        <div className="project-detail-header">
          <h1>Portfolio Dashboard</h1>
        </div>
        <p style={{ marginTop: '2em', fontSize: '1.3em', fontWeight: 700 }}>Error: {error.message}</p>
        {viewAsEmail && (
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem(CACHE_KEY);
              localStorage.removeItem(CACHE_TIME_KEY);
              localStorage.removeItem(CACHE_EMAIL_KEY);
              sessionStorage.removeItem(VIEW_AS_EMAIL_KEY);
              setViewAsInput('');
              setViewAsEmail('');
              navigate('/');
            }}
            style={{ marginTop: 16, padding: '10px 20px', borderRadius: 8, border: '1px solid #0a2740', background: '#0a2740', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
          >
            &#8592; Back to Home
          </button>
        )}
        {viewAsControls}
      </div>
    );
  }

  return (
    <>
      <div className="project-detail-header">
        <h1>Portfolio Dashboard</h1>
      </div>
      {data && data.data && data.data.length > 0 ? (
        <>
          <div className="portfolio-overview">
          <div className="portfolio-map">
            <MapComponent
              projects={
                filteredProjects && filteredProjects.length > 0
                  ? filteredProjects.map(project => ({
                      ...project,
                      address: [
                        project.Building_Formula__c,
                        project.Building_City__c,
                        project.Building_State__c
                      ].filter(Boolean).join(', '),
                      name: project.Project_Name__c || '',
                    }))
                  : []
              }
              services={data && data.services ? data.services : []}
              roster={data && data.roster ? data.roster : []}
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} // || 'Dont hardocde API key, use env variable'}
            />
          </div>
          <PortfolioSummary projects={data.data} onMetricClick={handleSummaryMetricClick} />
          </div>
          <p className="project-filter-guidance" id="project-filter-guidance">Text filters update as you type; no need to press Enter.</p>
          <div className="custom-table-container" style={{ marginTop: 40, marginBottom: 40 }}>
            <table className="custom-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 18px', background: '#f8fafc', borderRadius: '16px', fontSize: '1.1rem' }}>
              <thead>
                <tr style={{background:'#fff', boxShadow:'0 2px 8px rgba(0,0,0,0.04)'}}>
                  <td style={{borderTopLeftRadius:10, borderBottomLeftRadius:10, padding:'14px 18px'}}>
                    <input
                      type="text"
                      aria-label="Filter by Project Details"
                      aria-describedby="project-filter-guidance"
                      value={filters['Project_Name__c'] || ''}
                      onChange={e => handleFilterChange('Project_Name__c', e.target.value)}
                      placeholder="Filter by Project Details"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1em', background:'#f8fafc', color:'#1e293b', textAlign:'left' }}
                    />
                  </td>
                  <td style={{padding:'14px 18px'}}>
                    <input
                      type="text"
                      aria-label="Filter by Contacts"
                      aria-describedby="project-filter-guidance"
                      value={filters['Client Name'] || ''}
                      onChange={e => handleFilterChange('Client Name', e.target.value)}
                      placeholder="Filter by Contacts"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1em', background:'#f8fafc', color:'#1e293b', textAlign:'left' }}
                    />
                  </td>
                  <td style={{padding:'14px 18px'}}>
                    <select
                      value={filters['Project_State__c'] || ''}
                      title={filters['Project_State__c'] || 'All Billing States'}
                      onChange={e => handleFilterChange('Project_State__c', e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1em', background:'#f8fafc', color:'#1e293b', textAlign:'left' }}
                    >
                      <option value="">All Billing States</option>
                      <option value="Project Hold">Project Hold</option>
                      <option value="Accounting Hold - Past Due Unpaid Invoices">Accounting Hold - Past Due Unpaid Invoices</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Complete">Complete</option>
                      <option value="In Progress">In Progress</option>
                    </select>
                  </td>
                  <td style={{padding:'14px 18px'}}>
                    <select
                      value={filters['Project_Status__c'] || ''}
                      title={filters['Project_Status__c'] || 'All Statuses'}
                      onChange={e => handleFilterChange('Project_Status__c', e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1em', background:'#f8fafc', color:'#1e293b', textAlign:'left' }}
                    >
                      <option value="">All Statuses</option>
                      {projectStatuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{padding:'14px 18px'}}>
                    <select
                      aria-label="Filter by Project Health"
                      value={filters['Project_Health__c'] || ''}
                      title={filters['Project_Health__c'] || 'All Project Health'}
                      onChange={e => handleFilterChange('Project_Health__c', e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1em', background:'#f8fafc', color:'#1e293b', textAlign:'left' }}
                    >
                      <option value="">All Project Health</option>
                      {projectHealthStates.map(health => (
                        <option key={health.label} value={health.label}>{health.label}</option>
                      ))}
                      <option value="N/A">Health unspecified</option>
                    </select>
                  </td>
                  <th scope="col" aria-sort={endDateSort === 'asc' ? 'ascending' : endDateSort === 'desc' ? 'descending' : 'none'} style={{padding:'14px 18px'}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <select
                        aria-label="Filter by Expected End Date"
                        value={expectedEndDateFilter}
                        title={expectedEndDateFilter === 'past-due' ? 'Past Due Dates' : 'All Expected End Dates'}
                        onChange={e => setExpectedEndDateFilter(e.target.value)}
                        style={{ flex: 1, minWidth: 0, width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1em', background:'#f8fafc', color:'#1e293b', textAlign:'left' }}
                      >
                        <option value="">All Expected End Dates</option>
                        <option value="past-due">Past Due Dates</option>
                      </select>
                      <button
                        type="button"
                        className="project-date-sort"
                        aria-label="Sort by Expected End Date"
                        onClick={() => setEndDateSort(direction => direction === 'asc' ? 'desc' : 'asc')}
                      >
                        <span aria-hidden="true">{endDateSort === 'asc' ? '\u25b2' : endDateSort === 'desc' ? '\u25bc' : '\u25b2\u25bc'}</span>
                      </button>
                    </div>
                  </th>
                  <td style={{borderTopRightRadius:10, borderBottomRightRadius:10, padding:'14px 18px'}}>
                    <span style={{color:'#64748b', fontWeight:600, fontSize:'0.98em', letterSpacing:'0.02em', verticalAlign:'middle'}}>
                      {filteredProjects.length} projects
                    </span>
                  </td>
                </tr>
                
              </thead>
              <tbody>
                {sortedProjects.length === 0 && (
                  <tr>
                    <td colSpan={7} role="status">No projects match the current filters. Clear or adjust the filters to see more projects.</td>
                  </tr>
                )}
                {sortedProjects.map((project, idx) => (
                  <tr key={idx} style={{background:'#fff', boxShadow:'0 2px 8px rgba(0,0,0,0.04)'}}>
                    <td style={{borderTopLeftRadius:10, borderBottomLeftRadius:10, padding:'14px 18px', fontWeight:600, color:'#1e293b'}}>
                      <div style={{ fontSize: '0.75em', color: '#64748b', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                        {project.Name}
                      </div>
                      <div style={{ fontSize: '1.18em' }}>{project.Billing_Company_Name__r && project.Billing_Company_Name__r.Name}</div>
                      <div style={{ fontSize: '1.18em' }}>{project.Building_Formula__c + ((', ' + project.Floor_s__c) || '')  + ((', ' + project.Building_City__c) || '')  + ((', ' + project.Building_State__c) || '')}</div>
                      <div style={{ fontSize: '0.75em', color: '#64748b', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                        {project.Project_Name__c}
                      </div>
                      <div style={{ fontSize: '0.75em', color: '#64748b', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                        Client Project # {project.Client_Project__c || 'N/A'}
                      </div>
                    </td>
                    <td style={{padding:'14px 18px'}}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <div style={{ fontSize: '0.98em', color: '#64748b', fontWeight: 700, marginBottom: 2 }}>
                            Milrose PM:
                          </div>
                          <div style={{ fontSize: '1.08em', color: '#1e293b', fontWeight: 600, marginBottom: 8 }}>
                            {(project.Project_Manager__r && project.Project_Manager__r.Name) || (project.Owner && project.Owner.Name) || 'N/A'}
                          </div>
                          <div style={{ fontSize: '0.98em', color: '#64748b', fontWeight: 700, marginBottom: 2 }}>
                            Client PM:
                          </div>
                          <div style={{ fontSize: '1.08em', color: '#1e293b', fontWeight: 600 }}>
                            {data && data.roster && Array.isArray(data.roster) && data.roster.filter(rosterItem => rosterItem.Role__c === 'Client Project Manager' && rosterItem.Project_Profile__r && rosterItem.Project_Profile__r.Id === project.Id).length > 0 ? (
                              data.roster.filter(rosterItem => rosterItem.Role__c === 'Client Project Manager' && rosterItem.Project_Profile__r && rosterItem.Project_Profile__r.Id === project.Id).slice(0, 2).map((pm, idx) => (
                                <div key={idx}>{pm.Name_Full__c || 'N/A'}</div>
                              ))
                            ) : (
                              'N/A'
                            )}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.98em', color: '#64748b', fontWeight: 700, marginBottom: 2 }}>
                            Regional Lead:
                          </div>
                          <div style={{ fontSize: '1.08em', color: '#1e293b', fontWeight: 600, marginBottom: 8 }}>
                            {data && data.roster && Array.isArray(data.roster) && data.roster.filter(rosterItem => rosterItem.Role__c === 'Regional Lead' && rosterItem.Project_Profile__r && rosterItem.Project_Profile__r.Id === project.Id).length > 0 ? (
                              data.roster.filter(rosterItem => rosterItem.Role__c === 'Regional Lead' && rosterItem.Project_Profile__r && rosterItem.Project_Profile__r.Id === project.Id).slice(0, 2).map((pm, idx) => (
                                <div key={idx}>{pm.Name_Full__c || 'N/A'}</div>
                              ))
                            ) : (
                              'N/A'
                            )}
                          </div>
                          <div style={{ fontSize: '0.98em', color: '#64748b', fontWeight: 700, marginBottom: 2 }}>
                            Team Lead:
                          </div>
                          <div style={{ fontSize: '1.08em', color: '#1e293b', fontWeight: 600 }}>
                            {data && data.roster && Array.isArray(data.roster) && data.roster.filter(rosterItem => rosterItem.Role__c === 'Team Lead' && rosterItem.Project_Profile__r && rosterItem.Project_Profile__r.Id === project.Id).length > 0 ? (
                              data.roster.filter(rosterItem => rosterItem.Role__c === 'Team Lead' && rosterItem.Project_Profile__r && rosterItem.Project_Profile__r.Id === project.Id).slice(0, 2).map((pm, idx) => (
                                <div key={idx}>{pm.Name_Full__c || 'N/A'}</div>
                              ))
                            ) : (
                              'N/A'
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{padding:'14px 18px'}}>
                      {(() => {
                        
                        let state = project.Project_State__c;
                        if (!knownStates.includes(state)) {
                          state = 'In Progress';
                        }
                        let bg = '', color = '#fff', label = '', border = 'none';
                        if (state === 'Project Hold') {
                          bg = '#facc15'; color = '#92400e'; label = 'Project Hold';
                        } else if (state === 'Accounting Hold - Past Due Unpaid Invoices') {
                          bg = '#f87171'; color = '#fff'; label = 'Hold - Past Due Invoices';
                        } else if (state === 'Cancelled') {
                          bg = '#fb923c'; color = '#fff'; label = 'Cancelled';
                        } else if (state === 'Complete') {
                          bg = '#0a2740'; color = '#fff'; label = 'Complete';
                        } else {
                          bg = '#fff';
                          color = '#0a2740';
                          label = 'In Progress';
                          border = '2px solid #0a2740';
                        }
                          // Use shared statusExplanations
                          const tooltipText = statusExplanations[label] || '';
                          return (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '2.4em', fontSize: '0.98em', lineHeight: '1.2em', color: '#64748b', fontWeight: 700, marginBottom: 2, textAlign: 'center' }}>Billing State</div>
                              <div style={{ position: 'relative', width: '100%' }}>
                                <span
                                  className={`portal-status-badge billing-badge ${state === 'Project Hold' || state === 'Cancelled' ? 'is-amber' : state === 'Accounting Hold - Past Due Unpaid Invoices' ? 'is-red' : state === 'Complete' ? 'is-neutral' : 'is-green'}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: bg,
                                    color: color,
                                    fontWeight: 700,
                                    fontSize: '0.98em',
                                    borderRadius: '8px',
                                    height: '52px',
                                    minWidth: '120px',
                                    maxWidth: '180px',
                                    width: '100%',
                                    margin: '0 auto',
                                    letterSpacing: '0.02em',
                                    textTransform: 'uppercase',
                                    textAlign: 'center',
                                    border: border,
                                    cursor: 'pointer',
                                    position: 'relative',
                                  }}
                                  onMouseEnter={e => {
                                    const tooltip = e.currentTarget.nextSibling;
                                    if (tooltip) tooltip.style.opacity = 1;
                                  }}
                                  onMouseLeave={e => {
                                    const tooltip = e.currentTarget.nextSibling;
                                    if (tooltip) tooltip.style.opacity = 0;
                                  }}
                                >
                                  {label}
                                </span>
                                <span
                                  style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '110%',
                                    transform: 'translateX(-50%)',
                                    background: '#0a2740',
                                    color: '#fff',
                                    padding: '8px 14px',
                                    borderRadius: '8px',
                                    fontSize: '0.95em',
                                    fontWeight: 400,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                    whiteSpace: 'pre-line',
                                    zIndex: 10,
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    transition: 'opacity 0.2s',
                                    minWidth: '220px',
                                    maxWidth: '320px',
                                  }}
                                >
                                  {tooltipText}
                                </span>
                              </div>
                            </>
                          );
                      })()}
                    </td>
                    <td style={{padding:'14px 18px'}}>
                      {(() => {
                        // Normalize label for matching
                        let lifecycleLabel = project.Project_Status__c || 'N/A';
                        const normalized = lifecycleLabel.trim().toLowerCase();
                        if (normalized === 'services fulfilled') {
                          lifecycleLabel = 'Services Fulfilled';
                        } else if (normalized === 'under construction') {
                          lifecycleLabel = 'Under Construction';
                        } else if (normalized === 'rfi in circulation') {
                          lifecycleLabel = 'RFI In Circulation';
                        }
                        const lifecycleTooltip = lifecycleExplanations[lifecycleLabel] || '';
                        return (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '2.4em', fontSize: '0.98em', lineHeight: '1.2em', color: '#64748b', fontWeight: 700, marginBottom: 2, textAlign: 'center' }}>Current Project Status</div>
                            <div style={{ position: 'relative', width: '100%' }}>
                              <span
                                className="portal-status-badge lifecycle-badge"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#e0f2fe',
                                  color: '#1e293b',
                                  fontWeight: 700,
                                  fontSize: '0.98em',
                                  borderRadius: '8px',
                                  height: '52px',
                                  minWidth: '120px',
                                  maxWidth: '180px',
                                  width: '100%',
                                  margin: '0 auto',
                                  letterSpacing: '0.02em',
                                  textTransform: 'uppercase',
                                  textAlign: 'center',
                                  cursor: lifecycleTooltip ? 'pointer' : 'default',
                                  position: 'relative',
                                }}
                                onMouseEnter={e => {
                                  const tooltip = e.currentTarget.nextSibling;
                                  if (tooltip) tooltip.style.opacity = 1;
                                }}
                                onMouseLeave={e => {
                                  const tooltip = e.currentTarget.nextSibling;
                                  if (tooltip) tooltip.style.opacity = 0;
                                }}
                              >
                                {lifecycleLabel}
                              </span>
                              <span
                                style={{
                                  position: 'absolute',
                                  left: '50%',
                                  top: '110%',
                                  transform: 'translateX(-50%)',
                                  background: '#0a2740',
                                  color: '#fff',
                                  padding: '8px 14px',
                                  borderRadius: '8px',
                                  fontSize: '0.95em',
                                  fontWeight: 400,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                  whiteSpace: 'pre-line',
                                  zIndex: 10,
                                  opacity: 0,
                                  pointerEvents: 'none',
                                  transition: 'opacity 0.2s',
                                  minWidth: '220px',
                                  maxWidth: '320px',
                                }}
                              >
                                {lifecycleTooltip}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td style={{padding:'14px 18px'}}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '2.4em', fontSize: '0.98em', lineHeight: '1.2em', color: '#64748b', fontWeight: 700, marginBottom: 2, textAlign: 'center' }}>Project Health</div>
                      {(() => {
                        const value = project.Project_Health__c?.trim() || 'N/A';
                        const health = projectHealthStates.find(state => state.label.toLowerCase() === value.toLowerCase());
                        return (
                          <span className={`project-health-badge ${health?.className || 'unspecified'}`}>
                            {health?.label || value}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{padding:'14px 18px'}}>
                      <div style={{ fontSize: '1.08em', color: '#1e293b', fontWeight: 600, textAlign: 'center' }}>
                        {formatExpectedEndDate(project.Project_Expected_End_Date__c)}
                      </div>
                    </td>
                    <td style={{borderTopRightRadius:10, borderBottomRightRadius:10, padding:'14px 18px', textAlign:'right'}}>
                      <Link
                        to={`/project/${project.Id}`}
                        style={{
                          color: '#1e293b',
                          fontWeight: 600,
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '1.05em',
                          letterSpacing: '0.01em',
                        }}
                        onClick={() => {
                          localStorage.setItem('selectedProject', JSON.stringify({
                            ...project,
                            projectRoster: data.roster.filter(rosterItem => rosterItem.Project_Profile__r.Id === project.Id),
                            projectServices: data.services.filter(serviceItem => serviceItem.Project_Profile__r.Id === project.Id),
                            milrosePM: (project.Project_Manager__r && project.Project_Manager__r.Name) || (project.Owner && project.Owner.Name) || 'N/A',
                            milrosePMEmail: (project.Project_Manager__r && project.Project_Manager__r.Email) || (project.Owner && project.Owner.Email) || 'N/A',
                            milrosePMPhone: (project.Project_Manager__r && project.Project_Manager__r.Phone) || (project.Owner && project.Owner.Phone) || 'N/A',
                            arCoordinatorName: project.A_R_Coordinator_Full_Name__c,
                            arCoordinatorEmail: project.A_R_Coordinator_Email__c,
                            arCoordinatorPhone: project.A_R_Coordinator_Phone__c,
                            notes: project.Current_Status__c,
                            currentStatus: project.Project_Status__c,
                            groundUp: project.Ground_Up__c,
                            rollout: project.Rollout__c,
                            projectStatus: project.Project_Status__c,
                            clientName: (project.Billing_Company_Name__r && project.Billing_Company_Name__r.Name),
                          }));
                        }}
                      >
                        View Details <span style={{fontSize:'1.2em', fontWeight:700, marginLeft:2, color:'#1e293b'}}>&#8594;</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Second table removed as requested */}
        </>
      ) : (
        <div>
          <p style={{ marginTop: '2em', fontSize: '1.3em', fontWeight: 700 }}>
            No records found for user: {viewAsEmail || email}.
            {isMilroseUser ? ' Use View as to impersonate another user.' : ''}
          </p>
          {viewAsEmail && (
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(CACHE_KEY);
                localStorage.removeItem(CACHE_TIME_KEY);
                localStorage.removeItem(CACHE_EMAIL_KEY);
                setViewAsInput('');
                setViewAsEmail('');
                navigate('/');
              }}
              style={{ marginTop: 16, padding: '10px 20px', borderRadius: 8, border: '1px solid #0a2740', background: '#0a2740', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
            >
              &#8592; Back to Home
            </button>
          )}
        </div>
      )}
      {viewAsControls}
    </>
  );
}

export default ApiComponent;